<script setup lang="ts" generic="TabType extends Tab | PageTab">
import type { PageTab } from '../pages/BasePage/types';
import TabComponent from './Tab.vue';
import { type Tab, type TabDefinition } from '@/components/Tabs/types.ts';

type TabsProps = {
  tabs: TabDefinition<TabType> | undefined;
  withShadow?: boolean;
};

const props = withDefaults(defineProps<TabsProps>(), {
  withShadow: false,
});

const emits = defineEmits<{
  select: [key: string, index: number, tab: Tab];
}>();

const modelValue = defineModel('modelValue', { type: String, default: '' });

function select(key: string, index: number, tab: Tab) {
  props.tabs?.[key]?.handleSelect?.(key, index, tab);
  emits('select', key, index, tab);
}

function resetScrollY() {
  const element = document.getElementById('appContent') as HTMLElement;
  if (element) element.scrollTop = 0;
}
</script>

<template>
  <v-tabs
    v-model="modelValue"
    :mandatory="true"
    class="tabs"
    :class="{ 'tabs--with-shadow': withShadow }"
    @update:modelValue="resetScrollY"
  >
    <template v-for="(tab, key, index) in tabs" :key="tab.key">
      <TabComponent :tab="tab" :value="key" @click="select(key, index, tab)" />
    </template>
  </v-tabs>
</template>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/mixins';

:deep(.v-tab__slider) {
  /*
  * no other way to set this
  * @url: https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/components/VTabs/VTab.sass
  */
  border-bottom-left-radius: variables.$tab-slider-size + 1;
  border-bottom-right-radius: variables.$tab-slider-size + 1;
}

.tabs {
  position: relative;
  top: variables.$tab-slider-size;
  z-index: 9;
  height: calc(100% + #{variables.$tab-slider-size * 2});

  &--with-shadow {
    @include mixins.shadow-bottom(5);
    &:after {
      bottom: 4px;
    }
  }
}
</style>
